import React from 'react';
import { useViewport } from '~/utils/viewport-provider';
import { VIEWPORT } from '~/utils/constants';
import { TextTemplate } from '~/components/reusable/TextTemplate';

function getColSpan(type?: string) {
  switch (type) {
    case 'small':
      return 'col-span-2';
    case 'medium':
      return 'col-span-3 md:col-span-2';
    case 'large':
      return 'col-span-6 md:col-span-2';
    default:
      return 'col-span-2';
  }
}

function getAlignment(align?: string) {
  switch (align) {
    case 'center':
      return 'items-center text-center';
    case 'right':
      return 'items-end text-right';
    default:
      return 'items-start text-left';
  }
}

function getLayout(image?: any, padding_bottom?: boolean) {
  const layout = {
    flow: 'flex-col',
    imageStart: false,
    imageEnd: false,
    paddingReset: '',
  };
  if (image && image.src) {
    layout.flow =
      image.position === 'left' || image.position === 'right'
        ? 'flex-row'
        : 'flex-col';
    layout.imageStart = image.position === 'top' || image.position === 'left';
    layout.imageEnd = image.position === 'bottom' || image.position === 'right';
    layout.paddingReset = '';
    if (!padding_bottom) {
      if (image.position === 'top') {
        layout.paddingReset = 'pt-0';
      } else {
        layout.paddingReset = 'pb-0';
      }
    }
  }
  return layout;
}

function isInvisible(
  device: keyof typeof VIEWPORT,
  visibility?: FeatureDeviceVisibility[]
) {
  if (!visibility) {
    return false;
  } else if (visibility.length > 0) {
    return !visibility.includes(device);
  }
  return false;
}

export const FeaturesGrid: React.FC<FeaturesGrid> = ({
  content_alignment,
  rounded_corners,
  features_grid,
}) => {
  const { viewport, isMobile } = useViewport();

  const alignment = getAlignment(content_alignment);
  const corners =
    typeof rounded_corners === undefined || rounded_corners === ''
      ? 'border-rounded-strong'
      : rounded_corners;
  return (
    <div
      className={`grid max-w-xl grid-cols-6 items-start justify-between gap-6 xl:max-w-lg lg:max-w-md md:grid-cols-2 md:px-4`}
    >
      {features_grid.map((feature, featureIndex) => {
        const {
          visibility,
          padding_bottom = false,
          offset_top = 0,
          size_type,
          icon = { src: '', size: 0 },
          template_pretitle,
          template_title,
          template_text,
          border_settings,
          background_color = 'bg-blue-light-gradient',
          image,
        } = feature;
        const colSpan = getColSpan(size_type);
        // Image is optional, so we need to check if it exists before using it
        // otherwise we'll get an error or weird layout
        const { flow, imageStart, imageEnd, paddingReset } = getLayout(
          image,
          padding_bottom
        );

        if (isInvisible(viewport.device, visibility)) {
          return null;
        }

        const featureStyle = {
          background: background_color,
          border: `${border_settings?.size || 0}px solid ${border_settings?.color || 'transparent'}`,
          marginTop: isMobile ? 0 : `${offset_top || 0}rem`,
        };

        return (
          <div
            key={featureIndex}
            className={`relative flex flex-row justify-between gap-12 overflow-hidden p-10 lg:flex-col ${corners} ${flow} ${paddingReset} ${colSpan} ${alignment}`}
            style={featureStyle}
          >
            {imageStart && (
              <img
                src={image!.src}
                alt={image!.alt}
                className='h-auto max-h-[52rem] max-w-full'
                style={{
                  maxWidth: image?.image_max_width
                    ? `${image.image_max_width}rem`
                    : '',
                  maxHeight: image?.image_max_height
                    ? `${image.image_max_height}rem`
                    : '',
                }}
              />
            )}
            <div className='flex w-full flex-col gap-4'>
              {icon && (
                <img
                  src={icon.src}
                  alt=''
                  style={{
                    height: icon.size ? `${icon.size}rem` : 'auto',
                    width: icon.size ? `${icon.size}rem` : 'auto',
                  }}
                  className='mb-4'
                />
              )}
              {template_pretitle && (
                <TextTemplate
                  {...template_pretitle}
                  as='h3'
                />
              )}
              {template_title && (
                <TextTemplate
                  {...template_title}
                  as='h3'
                />
              )}
              {template_text && (
                <TextTemplate
                  {...template_text}
                  as='p'
                />
              )}
            </div>
            {imageEnd && (
              <img
                src={image!.src}
                alt={image!.alt}
                className='mx-auto h-auto max-h-[52rem] max-w-full'
                style={{
                  maxWidth: image?.image_max_width
                    ? `${image.image_max_width}rem`
                    : '',
                  maxHeight: image?.image_max_height
                    ? `${image.image_max_height}rem`
                    : '',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/*
(
  <p
    style={{ color: template_text.color }}
    dangerouslySetInnerHTML={{
      __html: template_text.text,
    }}
  />
)
*/
